import { useState } from 'react';
import { CheckCircle2, MessageSquarePlus, X } from 'lucide-react';
import {
  submitPortalFeedback,
  type PortalFeedbackPage,
  type PortalFeedbackIssue,
  type PortalFeedbackImpact
} from '@altios/api-client';
import type { PortalMoneySection, PortalView } from '../lib/portalModel';

const issueOptions: { value: PortalFeedbackIssue; label: string }[] = [
  { value: 'navigation', label: "I couldn't find something" },
  { value: 'broken', label: "Something didn't work" },
  { value: 'unclear', label: 'The instructions were confusing' },
  { value: 'steps', label: 'Too many steps to finish' },
  { value: 'idea', label: 'I have an improvement idea' }
];
const impactOptions: { value: PortalFeedbackImpact; label: string }[] = [
  { value: 'blocked', label: "I couldn't finish" },
  { value: 'slow', label: 'It slowed me down' },
  { value: 'idea', label: 'Just a suggestion' }
];
const pageLabels: Record<PortalFeedbackPage, string> = {
  home: 'Home', selections: 'Selections', messages: 'Messages', schedule: 'Schedule',
  files: 'Files', progress: 'Project progress', more: 'More',
  'money/overview': 'Payments & agreements — overview',
  'money/estimates': 'Payments & agreements — estimate',
  'money/agreements': 'Payments & agreements — agreement',
  'money/changes': 'Payments & agreements — project changes',
  'money/payments': 'Payments & agreements — payments'
};

type Props = {
  projectId: string;
  email: string;
  view: PortalView;
  moneySection: PortalMoneySection;
};

export function PortalFeedback({ projectId, email, view, moneySection }: Props) {
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState<PortalFeedbackIssue>('navigation');
  const [impact, setImpact] = useState<PortalFeedbackImpact>('slow');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const page: PortalFeedbackPage = view === 'money' ? `money/${moneySection}` : view;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setError('');
    setSent(false);
  };

  async function submit() {
    const cleanNote = note.trim();
    if (busy || cleanNote.length < 8) return;
    setBusy(true);
    setError('');
    try {
      await submitPortalFeedback({ projectId, email, page, issueType, impact, note: cleanNote });
      setSent(true);
      setNote('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Feedback could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return <>
    <footer className="cp-feedback-footer">
      <span>Help us make the Customer Portal easier to use.</span>
      <button type="button" onClick={() => { setSent(false); setError(''); setOpen(true); }}>
        <MessageSquarePlus size={16} /> Suggest an improvement or report a problem
      </button>
    </footer>
    {open ? <div className="cp-feedback-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="cp-feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="cp-feedback-heading">
        <header>
          <div><span className="cp-eyebrow">Customer feedback</span><h2 id="cp-feedback-heading">Help us improve the Portal</h2></div>
          <button type="button" aria-label="Close feedback" disabled={busy} onClick={close}><X size={19} /></button>
        </header>
        {sent ? <div className="cp-feedback-done" role="status"><CheckCircle2 size={27} /><strong>Feedback received</strong><p>We'll look for recurring problems across projects to decide where to improve the experience.</p><button type="button" onClick={close}>Close</button></div> : <>
          <p>Tell us what made a task harder or what would work better. This isn't an urgent support channel; use Messages if you need a response from Altios.</p>
          <div className="cp-feedback-field"><span>Where were you?</span><strong>{pageLabels[page]}</strong><small>We'll attach this page automatically.</small></div>
          <label className="cp-feedback-field"><span>What best describes it?</span>
            <select value={issueType} onChange={(event) => { const next = event.target.value as PortalFeedbackIssue; setIssueType(next); if (next === 'idea') setImpact('idea'); else if (impact === 'idea') setImpact('slow'); }}>
              {issueOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="cp-feedback-field"><span>How much did this affect you?</span>
            <select value={impact} onChange={(event) => setImpact(event.target.value as PortalFeedbackImpact)}>
              {impactOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="cp-feedback-field"><span>What happened or what should change?</span>
            <textarea rows={4} maxLength={1200} value={note} onChange={(event) => setNote(event.target.value)} placeholder="For example: I had to look in three places to find the agreement I needed to sign." />
          </label>
          <small className="cp-feedback-privacy">No contact details needed. Reports are grouped by page and problem type to highlight common obstacles.</small>
          {error ? <p className="cp-feedback-error" role="alert">{error}</p> : null}
          <div className="cp-feedback-actions">
            <button type="button" disabled={busy} onClick={close}>Cancel</button>
            <button type="button" disabled={busy || note.trim().length < 8} onClick={() => void submit()}>{busy ? 'Sending…' : 'Send feedback'}</button>
          </div>
        </>}
      </section>
    </div> : null}
  </>;
}
